import { EmailItem } from '../../components/List/Email/EmailList'
import { IObjectWithKey } from '@fluentui/react'
import { normalizeAxiosError, NormalizedError } from '../../reducers/errorReducer'
import { useContext, useState } from 'react'
import useQueueDispatch from '../queue/useQueueDispatch'
import { QueueJobKind } from '../queue/queueTypes'
import { EmailFilingParams } from './emailFilingTypes'
import { AxiosResponse } from 'axios'
import AuthorizationContext from '../../context/AuthorizationContext'

export type SelectableEmail = EmailItem & IObjectWithKey

export type ProcessEmail = { id?: string; references?: string[] }

export type FilterReferences = (references: string[]) => string[]

export const PROCESS_EMAIL_TIMEOUT_LIMIT = 10000
export const PROCESS_EMAIL_TIMEOUT_LENGTH = 5000

export default function useProcessEmail() {
    const { host: currentHost } = useContext(AuthorizationContext)
    const [, setGlobalErrors] = useState<NormalizedError[]>()
    const dispatch = useQueueDispatch<EmailFilingParams>(QueueJobKind.EmailFiling)

    const processEmail = (
        mailboxEmail: string,
        notificationEmail: string,
        selectedEmail: ProcessEmail,
        filterReferences: FilterReferences,
        isBatch: boolean,
    ) => {
        dispatch(selectedEmail.id, {
            baseURL: process.env.INBOXER_HOST,
            url: `/api/file-message/`,
            data: {
                messageId: selectedEmail.id,
                userEmail: mailboxEmail,
                notificationEmail: notificationEmail,
                hostKey: currentHost?.RowKey,
                references: filterReferences(selectedEmail.references || []),
                isBatch: isBatch,
            },
            mapDispatchResponse: (response: AxiosResponse<void>) => response.data,
        })
    }

    const processSelection = (
        mailboxEmail: string,
        notificationEmail: string,
        messages: ProcessEmail[],
        filterReferences: FilterReferences,
        init: () => void,
        exit: () => void,
    ) => {
        init()

        return Promise.allSettled(
            messages.map(message => processEmail(mailboxEmail, notificationEmail, message, filterReferences, messages.length > 1)),
        )
            .catch(error => setGlobalErrors([normalizeAxiosError(error)]))
            .finally(() => {
                const timeout = PROCESS_EMAIL_TIMEOUT_LENGTH * messages.length
                // Short timeout to allow for Exchange to process the changes.
                setTimeout(
                    () => {
                        exit()
                    },
                    timeout < PROCESS_EMAIL_TIMEOUT_LIMIT ? timeout : PROCESS_EMAIL_TIMEOUT_LENGTH,
                )
            })
    }

    return { processSelection }
}
